"use client";
import { useAlgoliaClient } from "@hooks/useAlgoliaClient";
import { AND_SYMBOL } from "@lib/constants/filtersConstants";
import type { FC } from "react";
import { Configure, InstantSearch, InstantSearchSSRProvider } from "react-instantsearch";
import type { InitialResults, InstantSearchOptions, StateMapping } from "instantsearch.js";
import { getAlgoliaUserToken } from "@lib/analytics/getAlgoliaUserToken";
import { useStore } from "@store/index";
import React from "react";

interface AlgoliaProviderProps {
    children: React.ReactNode;
    pagePath?: string;
    locale?: string;
    mode?: "products" | "stores";
    aroundLatLng?: string;
    facetFilters?: string[];
    customRouting?: InstantSearchOptions["routing"];
    hitsPerPage?: number;
    algoliaFallbackState?: InitialResults;
}

export const AlgoliaProvider: FC<AlgoliaProviderProps> = ({
    children,
    locale,
    mode,
    facetFilters,
    aroundLatLng,
    hitsPerPage,
    algoliaFallbackState = {},
}) => {
    const { indexName, searchClient } = useAlgoliaClient(locale, mode);
    const routing = {
        stateMapping: {
            stateToRoute(uiState) {
                const indexUiState = uiState[indexName] || {};
                const route = {
                    query: indexUiState.query,
                    sortBy: indexUiState.sortBy && indexUiState.sortBy.split(`${indexName}_`)[1],
                };

                const keys = Object.keys(indexUiState?.refinementList ?? {});

                keys?.forEach((facetName) => {
                    route[facetName] =
                        indexUiState?.refinementList &&
                        indexUiState?.refinementList[facetName] &&
                        indexUiState?.refinementList[facetName].join(AND_SYMBOL);
                });

                return route;
            },

            routeToState(routeState) {
                const refinementList = {};
                const facetNames = Object.keys(routeState).filter(
                    (it) => it !== "sortBy" && it !== "page" && it !== "query"
                );

                facetNames?.forEach((facetName) => {
                    const item = routeState[facetName];
                    if (!item) return;
                    if (Array.isArray(item)) {
                        item.forEach((element) => {
                            if (element?.includes(AND_SYMBOL)) {
                                refinementList[facetName] = refinementList[facetName]
                                    ? [...refinementList[facetName], ...element.split(AND_SYMBOL)]
                                    : element.split(AND_SYMBOL);
                            } else {
                                refinementList[facetName] = refinementList[facetName]
                                    ? [...refinementList[facetName], element]
                                    : [element];
                            }
                        });
                    } else {
                        refinementList[facetName] = item.split(AND_SYMBOL);
                    }
                });

                return {
                    [indexName]: {
                        sortBy: routeState.sortBy && `${indexName}_${routeState.sortBy}`,
                        page: routeState.page,
                        query: routeState.query,
                        refinementList,
                    },
                };
            },
        } as StateMapping<any, any>,
    };

    const me = useStore((state) => state.data.me);

    const algoliaUserToken = getAlgoliaUserToken(me);

    const getRouting = () => {
        if (typeof window === "undefined") return false;
        return routing;
    };

    return (
        <InstantSearchSSRProvider {...algoliaFallbackState}>
            <InstantSearch searchClient={searchClient} indexName={indexName} routing={getRouting()}>
                <Configure
                    // @ts-ignore
                    hitsPerPage={hitsPerPage}
                    aroundLatLng={aroundLatLng}
                    clickAnalytics
                    facetFilters={facetFilters ?? ["hasStock:true"]}
                    userToken={algoliaUserToken}
                />
                {children}
            </InstantSearch>
        </InstantSearchSSRProvider>
    );
};

export default AlgoliaProvider;
